"use client";

import type { COBEOptions } from "cobe";
import { useEffect, useState } from "react";
import { DesktopOnly } from "@/components/ui/desktop-only";
import { Globe } from "@/components/ui/globe";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import { TypingAnimation } from "@/components/ui/typing-animation";

interface HeroSectionProps {
  headline: string;
  description: string;
}

const GLOBE_MARKERS: { location: [number, number]; size: number }[] = [
  { location: [39.4168, -5.5], size: 0.2 },
  { location: [14.5995, 120.9842], size: 0.03 },
  { location: [19.076, 72.8777], size: 0.1 },
  { location: [23.8103, 90.4125], size: 0.05 },
  { location: [30.0444, 31.2357], size: 0.07 },
  { location: [39.9042, 116.4074], size: 0.08 },
  { location: [-23.5505, -46.6333], size: 0.1 },
  { location: [19.4326, -99.1332], size: 0.1 },
  { location: [40.7128, -74.006], size: 0.1 },
  { location: [34.6937, 135.5022], size: 0.05 },
  { location: [41.0082, 28.9784], size: 0.06 },
];

const HERO_GLOBE_LIGHT: COBEOptions = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.1,
  dark: 0,
  diffuse: 1.2,
  mapSamples: 16000,
  mapBrightness: 6,
  baseColor: [0.75, 0.45, 0.25],
  markerColor: [0.2, 0.15, 0.1],
  glowColor: [0.85, 0.55, 0.3],
  markers: GLOBE_MARKERS,
};

const HERO_GLOBE_DARK: COBEOptions = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.1,
  dark: 1,
  diffuse: 1.2,
  mapSamples: 16000,
  mapBrightness: 6,
  baseColor: [0.25, 0.2, 0.15],
  markerColor: [251 / 255, 100 / 255, 21 / 255],
  glowColor: [0.3, 0.25, 0.2],
  markers: GLOBE_MARKERS,
};

function DataNode({
  variant = "filled",
}: {
  variant?: "filled" | "outlined" | "square";
}) {
  if (variant === "outlined") {
    return (
      <div className="size-3 rounded-full border-2 border-primary bg-transparent shadow-[0_0_20px_var(--primary)]" />
    );
  }
  if (variant === "square") {
    return (
      <div className="size-2.5 border border-primary bg-primary/80 shadow-[0_0_20px_var(--primary)]" />
    );
  }
  return (
    <div className="size-3 rounded-full bg-primary/80 shadow-[0_0_20px_var(--primary)]" />
  );
}

export function HeroSection({ headline, description }: HeroSectionProps) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const updateTheme = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const telemetryWords = [
    "ESTADO: INGESTA EN TIEMPO REAL",
    "ESTADO: FUSIÓN MULTIFUENTE",
    "ESTADO: TRAZABILIDAD / AUDITORÍA",
    "ESTADO: ALERTAS OPERACIONALES",
  ];

  return (
    <section className="relative min-h-svh w-full overflow-hidden bg-background">
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,var(--background)_70%)] z-10 pointer-events-none" />

      {/* Scanline effect */}
      <div className="absolute inset-0 z-5 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 animate-scanline bg-linear-to-b from-transparent via-primary/5 to-transparent" />
      </div>

      {/* Globe + Orbiting circles - only loaded on desktop */}
      <DesktopOnly>
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="relative w-full max-w-[600px] aspect-square opacity-90 dark:opacity-60">
            <Globe config={isDark ? HERO_GLOBE_DARK : HERO_GLOBE_LIGHT} />
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center z-2 pointer-events-none">
          <div className="relative size-[600px] max-w-full">
            <OrbitingCircles radius={140} duration={26} speed={1}>
              <DataNode variant="filled" />
              <DataNode variant="outlined" />
              <DataNode variant="filled" />
            </OrbitingCircles>

            <OrbitingCircles radius={200} duration={18} reverse speed={1}>
              <DataNode variant="square" />
              <DataNode variant="filled" />
              <DataNode variant="outlined" />
              <DataNode variant="square" />
            </OrbitingCircles>

            <OrbitingCircles radius={260} duration={34} path={false} speed={1}>
              <DataNode variant="outlined" />
              <DataNode variant="filled" />
            </OrbitingCircles>
          </div>
        </div>
      </DesktopOnly>

      {/* Content overlay */}
      <div className="relative z-20 grid min-h-svh place-items-center px-6">
        <div className="flex flex-col items-center text-center max-w-3xl">
          {/* Glass panel behind text */}
          <div className="absolute inset-0 -m-4 sm:-m-8 rounded-lg bg-background/80 dark:bg-background/40 backdrop-blur-sm border border-border/50 -z-10" />

          {/* Eyebrow */}
          <span className="mb-6 font-mono text-xs uppercase tracking-[0.2em] text-muted">
            Inteligencia Soberana Europea
          </span>

          {/* Headline */}
          <h1 className="mb-6 font-sans text-3xl font-bold leading-[0.95] tracking-tight text-foreground md:text-5xl lg:text-6xl">
            {headline}
          </h1>

          {/* Description */}
          <p className="mb-8 max-w-[60ch] font-mono text-sm leading-relaxed text-muted md:text-base">
            {description}
          </p>

          {/* Telemetry status line */}
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <TypingAnimation
              words={telemetryWords}
              className="font-mono text-xs text-muted-foreground"
              duration={80}
              pauseDelay={2000}
              loop
              showCursor
              cursorStyle="block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
